// Privacy API Service for GDPR compliance (Art. 15, 17)
const API_BASE = '/api';

export interface PrivacySettings {
    marketing: boolean;
    analytics: boolean;
    affiliates: boolean;
    policyVersion?: string;
    updatedAt?: string;
}

export interface ExportJob {
    id: string;
    status: 'queued' | 'ready' | 'failed';
    sizeBytes?: number;
    expiresAt?: string;
    createdAt: string;
}

export const privacyApi = {
    /**
     * Start data export job (Art. 15)
     */
    async startExport(): Promise<{ jobId: string }> {
        const response = await fetch(`${API_BASE}/privacy/export`, {
            method: 'POST',
            credentials: 'include'
        });
        if (!response.ok) {
            const error = await response.json();
            throw error;
        }
        return response.json();
    },

    /**
     * Check export status
     */
    async getExportStatus(jobId: string): Promise<ExportJob> {
        const response = await fetch(`${API_BASE}/privacy/export/${jobId}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            const error = await response.json();
            throw error;
        }
        return response.json();
    },

    /**
     * Get secure download URL
     */
    getDownloadUrl(jobId: string, token: string): string {
        return `${API_BASE}/privacy/export/${jobId}/download?token=${token}`;
    },

    /**
     * Update privacy preferences with audit logging
     */
    async updateConsent(settings: Partial<PrivacySettings>, policyVersion: string): Promise<void> {
        const response = await fetch(`${API_BASE}/privacy/consent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ settings, policyVersion })
        });
        if (!response.ok) {
            const error = await response.json();
            throw error;
        }
    },

    /**
     * Delete all owned travel data (Trips, Versions, Chat)
     */
    async deleteOwnedTrips(): Promise<void> {
        const response = await fetch(`${API_BASE}/privacy/trips`, {
            method: 'DELETE',
            credentials: 'include'
        });
        if (!response.ok) {
            const error = await response.json();
            throw error;
        }
    },

    /**
     * Initiate account soft-deletion (7-day grace period, Art. 17)
     */
    async deleteAccount(): Promise<{ message: string }> {
        const response = await fetch(`${API_BASE}/privacy/account`, {
            method: 'DELETE',
            credentials: 'include'
        });
        if (!response.ok) {
            const error = await response.json();
            throw error;
        }
        return response.json();
    }
};
